/**
 * NextJS-compatible cross-domain storage adapter
 * Implements the same interface as the Vite app's storage system
 * for seamless session sharing between domains
 */

export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface SessionData {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  token_type: string;
  user: {
    id: string;
    email?: string;
    [key: string]: any;
  };
}

export interface CrossDomainStorageOptions {
  storageKey?: string;
  cookieDomain?: string;
  enablePostMessage?: boolean;
  enableDebug?: boolean;
}

/**
 * Cross-domain storage implementation for NextJS
 * Uses cookies for production cross-subdomain sharing
 * Uses localStorage with postMessage for development cross-port communication
 */
export class CrossDomainStorage implements StorageAdapter {
  private storageKey: string;
  private cookieDomain: string;
  private enablePostMessage: boolean;
  private enableDebug: boolean;
  private isServer: boolean;

  constructor(options: CrossDomainStorageOptions = {}) {
    this.storageKey = options.storageKey || "SUPABASE_SESSION";
    this.cookieDomain = options.cookieDomain || ".world-class.design";
    this.enablePostMessage = options.enablePostMessage ?? true;
    this.enableDebug = options.enableDebug ?? false;
    this.isServer = typeof window === "undefined";

    if (this.enableDebug && !this.isServer) {
      console.log("[CrossDomainStorage] Initialized with options:", {
        storageKey: this.storageKey,
        cookieDomain: this.cookieDomain,
        enablePostMessage: this.enablePostMessage,
        isServer: this.isServer,
      });
    }
  }

  /**
   * Get item from storage
   * Prioritizes cookies for cross-domain compatibility
   */
  getItem(key: string): string | null {
    if (this.isServer) {
      // Server-side: return null (handled by middleware)
      return null;
    }

    try {
      // First try cookies for cross-domain compatibility
      const cookieValue = this.getCookie(key);
      if (cookieValue) {
        if (this.enableDebug) {
          console.log("[CrossDomainStorage] Retrieved from cookie:", key);
        }
        return cookieValue;
      }

      // Fallback to localStorage
      const localValue = localStorage.getItem(key);
      if (localValue) {
        if (this.enableDebug) {
          console.log("[CrossDomainStorage] Retrieved from localStorage:", key);
        }
        return localValue;
      }

      return null;
    } catch (error) {
      if (this.enableDebug) {
        console.error("[CrossDomainStorage] Error getting item:", error);
      }
      return null;
    }
  }

  /**
   * Set item in storage
   * Uses both cookies and localStorage for maximum compatibility
   */
  setItem(key: string, value: string): void {
    if (this.isServer) {
      // Server-side: no-op (handled by middleware)
      return;
    }

    try {
      // Set in localStorage for local access
      localStorage.setItem(key, value);

      // Set in cookies for cross-domain sharing
      this.setCookie(key, value);

      // Broadcast to other domains via postMessage (development)
      if (this.enablePostMessage) {
        this.broadcastSessionUpdate("SESSION_LOGIN", value);
      }

      if (this.enableDebug) {
        console.log(
          "[CrossDomainStorage] Set item:",
          key,
          "Length:",
          value.length
        );
      }
    } catch (error) {
      if (this.enableDebug) {
        console.error("[CrossDomainStorage] Error setting item:", error);
      }
    }
  }

  /**
   * Remove item from storage
   * Clears both cookies and localStorage
   */
  removeItem(key: string): void {
    if (this.isServer) {
      // Server-side: no-op (handled by middleware)
      return;
    }

    try {
      // Remove from localStorage
      localStorage.removeItem(key);

      // Remove from cookies
      this.removeCookie(key);

      // Broadcast logout to other domains
      if (this.enablePostMessage) {
        this.broadcastSessionUpdate("SESSION_LOGOUT", null);
      }

      if (this.enableDebug) {
        console.log("[CrossDomainStorage] Removed item:", key);
      }
    } catch (error) {
      if (this.enableDebug) {
        console.error("[CrossDomainStorage] Error removing item:", error);
      }
    }
  }

  /**
   * Get cookie value by name
   */
  private getCookie(name: string): string | null {
    if (this.isServer) return null;

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      const cookieValue = parts.pop()?.split(";").shift();
      return cookieValue ? decodeURIComponent(cookieValue) : null;
    }
    return null;
  }

  /**
   * Set cookie with proper domain and security settings
   */
  private setCookie(name: string, value: string): void {
    if (this.isServer) return;

    const isProduction = this.isProductionEnvironment();
    const domain = isProduction ? this.cookieDomain : undefined;
    const secure = isProduction ? "; Secure" : "";
    const sameSite = isProduction ? "; SameSite=Lax" : "";

    // Handle large values by chunking
    const maxChunkSize = 4000; // Leave room for cookie metadata
    if (value.length > maxChunkSize) {
      this.setChunkedCookie(name, value, domain, secure, sameSite);
    } else {
      const encodedValue = encodeURIComponent(value);
      document.cookie = `${name}=${encodedValue}; Path=/; Max-Age=604800${
        domain ? `; Domain=${domain}` : ""
      }${secure}${sameSite}`;
    }
  }

  /**
   * Set chunked cookie for large values
   */
  private setChunkedCookie(
    name: string,
    value: string,
    domain: string | undefined,
    secure: string,
    sameSite: string
  ): void {
    const maxChunkSize = 4000;
    const chunks = Math.ceil(value.length / maxChunkSize);

    // Set chunk count
    document.cookie = `${name}_chunks=${chunks}; Path=/; Max-Age=604800${
      domain ? `; Domain=${domain}` : ""
    }${secure}${sameSite}`;

    // Set individual chunks
    for (let i = 0; i < chunks; i++) {
      const start = i * maxChunkSize;
      const end = Math.min(start + maxChunkSize, value.length);
      const chunk = value.substring(start, end);
      const encodedChunk = encodeURIComponent(chunk);
      document.cookie = `${name}_${i}=${encodedChunk}; Path=/; Max-Age=604800${
        domain ? `; Domain=${domain}` : ""
      }${secure}${sameSite}`;
    }
  }

  /**
   * Remove cookie
   */
  private removeCookie(name: string): void {
    if (this.isServer) return;

    const isProduction = this.isProductionEnvironment();
    const domain = isProduction ? this.cookieDomain : undefined;
    const domainPart = domain ? `; Domain=${domain}` : "";

    // Remove main cookie
    document.cookie = `${name}=; Path=/; Max-Age=0${domainPart}`;

    // Remove chunked cookies if they exist
    const chunks = this.getCookie(`${name}_chunks`);
    if (chunks) {
      const chunkCount = parseInt(chunks, 10);
      for (let i = 0; i < chunkCount; i++) {
        document.cookie = `${name}_${i}=; Path=/; Max-Age=0${domainPart}`;
      }
      document.cookie = `${name}_chunks=; Path=/; Max-Age=0${domainPart}`;
    }
  }

  /**
   * Broadcast session update to other domains via postMessage
   */
  private broadcastSessionUpdate(
    type: "SESSION_LOGIN" | "SESSION_LOGOUT" | "SESSION_REFRESH",
    data: string | null
  ): void {
    if (this.isServer || !this.enablePostMessage) return;

    try {
      const message = {
        type: "CROSS_DOMAIN_SESSION",
        action: type,
        data: data,
        timestamp: Date.now(),
        source: "nextjs-app",
      };

      // Broadcast to all possible Vite app origins
      const viteOrigins = [
        "http://localhost:8080",
        "https://app.world-class.design",
      ];

      viteOrigins.forEach((origin) => {
        if (window.parent !== window) {
          window.parent.postMessage(message, origin);
        }
        window.postMessage(message, origin);
      });

      if (this.enableDebug) {
        console.log("[CrossDomainStorage] Broadcasted session update:", type);
      }
    } catch (error) {
      if (this.enableDebug) {
        console.error(
          "[CrossDomainStorage] Error broadcasting session update:",
          error
        );
      }
    }
  }

  /**
   * Check if we're in production environment
   */
  private isProductionEnvironment(): boolean {
    if (this.isServer) {
      return process.env.NODE_ENV === "production";
    }

    return (
      window.location.hostname.includes("world-class.design") &&
      !window.location.hostname.includes("localhost")
    );
  }

  /**
   * Get session data with proper parsing
   */
  getSessionData(): SessionData | null {
    const sessionString = this.getItem(this.storageKey);
    if (!sessionString) return null;

    try {
      return JSON.parse(sessionString);
    } catch (error) {
      if (this.enableDebug) {
        console.error(
          "[CrossDomainStorage] Error parsing session data:",
          error
        );
      }
      return null;
    }
  }

  /**
   * Set session data with proper serialization
   */
  setSessionData(sessionData: SessionData): void {
    try {
      const sessionString = JSON.stringify(sessionData);
      this.setItem(this.storageKey, sessionString);
    } catch (error) {
      if (this.enableDebug) {
        console.error(
          "[CrossDomainStorage] Error setting session data:",
          error
        );
      }
    }
  }

  /**
   * Clear all session data
   */
  clearSession(): void {
    this.removeItem(this.storageKey);
  }

  /**
   * Check if session exists and is valid
   */
  isSessionValid(): boolean {
    const sessionData = this.getSessionData();
    if (!sessionData) return false;

    const now = Math.floor(Date.now() / 1000);
    return sessionData.expires_at > now;
  }
}

/**
 * Create a cross-domain storage instance
 */
export function createCrossDomainStorage(
  options?: CrossDomainStorageOptions
): CrossDomainStorage {
  return new CrossDomainStorage(options);
}

/**
 * Default cross-domain storage instance
 */
export const crossDomainStorage = createCrossDomainStorage({
  enableDebug: process.env.NODE_ENV === "development",
});
